import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import { Badge } from "@/components/ui/badge";
import { inr, num, pct, fmtDate } from "@/lib/api";
import { AlertTriangle, ShieldAlert, Info, TrendingUp } from "lucide-react";

const PALETTE = ["#1E3A8A", "#10B981", "#3B82F6", "#F59E0B", "#EF4444", "#8B5CF6", "#0EA5E9", "#84CC16"];

const Card = ({ title, children, testid, aside }) => (
  <div className="rounded-lg border border-border bg-white" data-testid={testid}>
    <div className="flex items-center justify-between border-b border-border px-5 py-3">
      <h3 className="font-display text-base font-semibold">{title}</h3>
      {aside}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Metric = ({ label, value, estimated, testid }) => (
  <div data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">
      {label} {estimated && <span className="ml-1 rounded bg-secondary px-1 py-0.5 text-[9px] font-semibold text-secondary-foreground">ESTIMATED</span>}
    </p>
    <p className="num mt-1 font-display text-xl font-bold tracking-tight">{value}</p>
  </div>
);

const stabilityColor = (s) =>
  ({ Strong: "bg-ok text-ok-foreground border-green-200",
     Moderate: "bg-warn text-warn-foreground border-warn-border",
     Weak: "bg-red-50 text-red-700 border-red-200" }[s] || "bg-muted text-muted-foreground");

export const AnalysisView = ({ record }) => {
  const a = record.analysis;
  const s = a.summary;
  const st = record.statement || {};

  return (
    <div className="space-y-6" data-testid="analysis-view">
      <div className="flex gap-3 rounded-lg border border-warn-border bg-warn p-4 text-xs text-warn-foreground"
           data-testid="analysis-disclaimer">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" />
        <p>
          Analytical estimates are generated from extracted bank-statement data and may contain OCR or
          classification errors. Users should verify important information against the original statement.
          Any financial or credit-related assessment should be independently reviewed and should not rely
          solely on this application.
        </p>
      </div>

      <Card title="Financial Snapshot" testid="financial-snapshot"
            aside={<Badge variant="outline" className={stabilityColor(s.cash_flow_stability)}>
              Cash Flow Stability: {s.cash_flow_stability}</Badge>}>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <Metric label="Statement Period" value={`${fmtDate(s.statement_period_from)} – ${fmtDate(s.statement_period_to)}`} testid="m-period" />
          <Metric label="Total Credits" value={inr(s.total_credits)} testid="m-total-credits" />
          <Metric label="Total Debits" value={inr(s.total_debits)} testid="m-total-debits" />
          <Metric label="Net Cash Flow" value={inr(s.net_cash_flow)} testid="m-net-flow" />
          <Metric label="Average Monthly Credits" value={inr(s.average_monthly_credits)} testid="m-avg-credits" />
          <Metric label="Average Monthly Debits" value={inr(s.average_monthly_debits)} testid="m-avg-debits" />
          <Metric label="Business Credits" value={inr(s.estimated_business_credits)} estimated testid="m-business-credits" />
          <Metric label="Existing EMI / month" value={inr(s.estimated_monthly_emi)} estimated testid="m-emi" />
          <Metric label="Average Balance" value={inr(s.average_monthly_balance)} testid="m-avg-balance" />
          <Metric label="Min / Max Balance" value={`${inr(s.min_balance)} / ${inr(s.max_balance)}`} testid="m-minmax" />
          <Metric label="Cash Deposit Ratio" value={pct(s.cash_deposit_ratio)} testid="m-cash-ratio" />
          <Metric label="Business Credit Ratio" value={pct(s.business_credit_ratio)} estimated testid="m-business-ratio" />
          <Metric label="Excluded From Analysis" value={`${s.excluded_count || 0} txn(s)`} testid="m-excluded-count" />
        </div>
        <div className="mt-5 grid gap-3 rounded-md bg-muted p-4 text-xs sm:grid-cols-4">
          <p><span className="text-muted-foreground">Bank:</span> <b>{st.bank_name || "Generic"}</b></p>
          <p><span className="text-muted-foreground">Account Holder:</span> <b>{st.account_holder || "—"}</b></p>
          <p><span className="text-muted-foreground">Account:</span> <b>{st.account_number_masked || "—"}</b></p>
          <p><span className="text-muted-foreground">IFSC:</span> <b>{st.ifsc || "—"}</b></p>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Monthly Credits & Debits" testid="chart-monthly">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend />
              <Bar dataKey="credits" name="Credits" fill="#10B981" radius={[3, 3, 0, 0]} />
              <Bar dataKey="debits" name="Debits" fill="#1E3A8A" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Net Cash Flow & Balance Trend" testid="chart-netflow">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={a.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="label" fontSize={11} />
              <YAxis fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
              <Tooltip formatter={(v) => inr(v)} />
              <Legend />
              <Line type="monotone" dataKey="net_flow" name="Net Flow" stroke="#3B82F6" strokeWidth={2} />
              <Line type="monotone" dataKey="closing_balance" name="Closing Balance" stroke="#1E3A8A" strokeWidth={2} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Category Distribution" testid="chart-categories">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={a.categories.slice(0, 8).map((c) => ({ name: c.category, value: c.total_debit + c.total_credit }))}
                   dataKey="value" nameKey="name" outerRadius={95}>
                {a.categories.slice(0, 8).map((c, i) => <Cell key={c.category} fill={PALETTE[i % PALETTE.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => inr(v)} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>
        <Card title="Transaction Type Distribution" testid="chart-types">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={a.transaction_types.slice(0, 10)} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" fontSize={11} />
              <YAxis type="category" dataKey="type" width={110} fontSize={11} />
              <Tooltip />
              <Bar dataKey="count" name="Transactions" fill="#3B82F6" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card title="Monthly Analysis" testid="monthly-table">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground">
              <th className="py-2 text-left">Month</th><th className="text-right">Credits</th>
              <th className="text-right">Debits</th><th className="text-right">Net Flow</th>
              <th className="text-right">Closing Balance</th><th className="text-right">Txns</th>
            </tr></thead>
            <tbody>
              {a.monthly.map((m) => (
                <tr key={m.month} className="border-b border-border/60">
                  <td className="py-2">{m.label}</td>
                  <td className="num text-right text-green-700">{inr(m.credits)}</td>
                  <td className="num text-right text-red-700">{inr(m.debits)}</td>
                  <td className="num text-right">{inr(m.net_flow)}</td>
                  <td className="num text-right">{inr(m.closing_balance)}</td>
                  <td className="num text-right">{m.transactions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Credit Assessment (Analytical)" testid="credit-assessment">
          <div className="grid gap-4 sm:grid-cols-2">
            <Metric label="Monthly Income" value={inr(a.credit_assessment.estimated_monthly_income)} estimated testid="ca-income" />
            <Metric label="Business Credits" value={inr(a.credit_assessment.estimated_business_credits)} estimated testid="ca-business" />
            <Metric label="Avg Monthly Credits" value={inr(a.credit_assessment.average_monthly_credits)} testid="ca-credits" />
            <Metric label="Avg Monthly Debits" value={inr(a.credit_assessment.average_monthly_debits)} testid="ca-debits" />
            <Metric label="Existing EMI" value={inr(a.credit_assessment.estimated_existing_emi)} estimated testid="ca-emi" />
            <Metric label="Avg Monthly Balance" value={inr(a.credit_assessment.average_monthly_balance)} testid="ca-balance" />
            <Metric label="Credit Volatility" value={a.credit_assessment.credit_volatility} testid="ca-volatility" />
            <Metric label="EMI-to-Income Ratio" value={a.credit_assessment.emi_to_income_label} testid="ca-emi-ratio" />
            <Metric label="Negative Balance Events" value={num(a.credit_assessment.negative_balance_events)} testid="ca-negative" />
            <Metric label="Bounce / Return Events" value={num(a.credit_assessment.bounce_events)} testid="ca-bounce" />
            <Metric label="Recurring Obligations" value={num(a.credit_assessment.recurring_obligations)} testid="ca-obligations" />
            <Metric label="Cash Deposit Ratio" value={pct(a.credit_assessment.cash_deposit_ratio)} testid="ca-cash-ratio" />
          </div>
          <div className="mt-5 rounded-md border border-border p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Cash Flow Stability factors
            </p>
            <ul className="mt-3 space-y-2 text-sm">
              {a.credit_assessment.stability_factors.map((f) => (
                <li key={f.factor} className="flex items-center justify-between gap-3">
                  <span className="text-muted-foreground">{f.factor}</span>
                  <span className="flex items-center gap-2">
                    <span className="num">{f.value}</span>
                    <Badge variant="outline" className={
                      f.impact === "Positive" ? "bg-ok text-ok-foreground border-green-200"
                        : f.impact === "Negative" ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-warn text-warn-foreground border-warn-border"}>{f.impact}</Badge>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-muted-foreground">
              This is an analytical tool, not an automated lending decision.
            </p>
          </div>
        </Card>

        <div className="space-y-6">
          <Card title="EMI Analysis (Estimated)" testid="emi-analysis">
            {a.emi.emis.length === 0 ? (
              <p className="text-sm text-muted-foreground">No recurring EMI pattern was identified.</p>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 text-left">Lender / Narration</th><th className="text-right">EMI</th>
                  <th className="text-right">Payments</th><th className="text-right">Last</th>
                </tr></thead>
                <tbody>
                  {a.emi.emis.map((e, i) => (
                    <tr key={i} className="border-b border-border/60">
                      <td className="max-w-[220px] truncate py-2">{e.lender}<br />
                        <span className="text-xs text-muted-foreground">{e.classification} · {e.frequency}</span></td>
                      <td className="num text-right">{inr(e.estimated_emi)}</td>
                      <td className="num text-right">{e.observed_payments}</td>
                      <td className="text-right text-xs">{fmtDate(e.last_payment_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <p className="mt-3 text-xs text-muted-foreground">{a.emi.note}</p>
          </Card>

          <Card title="Recurring Transactions" testid="recurring-table">
            {a.recurring.length === 0 ? <p className="text-sm text-muted-foreground">None detected.</p> : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 text-left">Transaction</th><th className="text-right">Avg Amount</th>
                  <th className="text-right">Freq</th><th className="text-right">Occ</th><th className="text-right">Last</th>
                </tr></thead>
                <tbody>
                  {a.recurring.slice(0, 12).map((r, i) => (
                    <tr key={i} className="border-b border-border/60">
                      <td className="max-w-[200px] truncate py-2">{r.description}</td>
                      <td className="num text-right">{inr(r.average_amount)}</td>
                      <td className="text-right text-xs">{r.frequency}</td>
                      <td className="num text-right">{r.occurrences}</td>
                      <td className="text-right text-xs">{fmtDate(r.last_date)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Card title="Cash Deposit Analysis" testid="cash-deposit-analysis">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Total</span><b className="num">{inr(a.cash_deposits.total)}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Count</span><b className="num">{a.cash_deposits.count}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Average</span><b className="num">{inr(a.cash_deposits.average)}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Largest</span><b className="num">{inr(a.cash_deposits.largest)}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Monthly average</span><b className="num">{inr(a.cash_deposits.monthly_average)}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Cash Deposit Ratio</span><b className="num">{pct(a.cash_deposits.ratio)}</b></div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">{a.cash_deposits.note}</p>
        </Card>
        <Card title="UPI Analysis" testid="upi-analysis">
          <div className="space-y-3 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">UPI Credits</span><b className="num">{inr(a.upi.credits)}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">UPI Debits</span><b className="num">{inr(a.upi.debits)}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Transactions</span><b className="num">{a.upi.count}</b></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Total value</span><b className="num">{inr(a.upi.total_value)}</b></div>
          </div>
          {a.upi.top_counterparties.length > 0 && (
            <>
              <p className="mt-4 text-xs uppercase tracking-[0.15em] text-muted-foreground">Top counterparties</p>
              <ul className="mt-2 space-y-1 text-sm">
                {a.upi.top_counterparties.map((c) => (
                  <li key={c.name} className="flex justify-between gap-3">
                    <span className="truncate text-muted-foreground">{c.name}</span><span className="num">{inr(c.value)}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Card>
        <Card title="Bank Charges" testid="bank-charges">
          <p className="num font-display text-2xl font-bold">{inr(a.bank_charges.total)}</p>
          <p className="text-xs text-muted-foreground">{a.bank_charges.count} charge entries detected</p>
          <ul className="mt-4 max-h-48 space-y-1 overflow-auto text-xs">
            {a.bank_charges.items.map((c, i) => (
              <li key={i} className="flex justify-between gap-3 border-b border-border/50 py-1">
                <span className="truncate text-muted-foreground">{fmtDate(c.date)} · {c.description}</span>
                <span className="num">{inr(c.amount, { decimals: 2 })}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <Card title="Balance Validation" testid="balance-validation"
              aside={<Badge variant="outline" className={a.balance_validation.status === "Passed"
                ? "bg-ok text-ok-foreground border-green-200" : "bg-red-50 text-red-700 border-red-200"}>
                {a.balance_validation.status}</Badge>}>
          <p className="text-sm text-muted-foreground">
            {a.balance_validation.checked} transactions checked · {a.balance_validation.mismatch_count} mismatch(es)
          </p>
          {a.balance_validation.mismatches.length > 0 && (
            <table className="mt-4 w-full text-sm">
              <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground">
                <th className="py-2 text-left">Date</th><th className="text-right">Expected</th>
                <th className="text-right">Statement</th><th className="text-right">Difference</th>
              </tr></thead>
              <tbody>
                {a.balance_validation.mismatches.slice(0, 12).map((m, i) => (
                  <tr key={i} className="border-b border-border/60">
                    <td className="py-2 text-xs">{fmtDate(m.date)}</td>
                    <td className="num text-right">{inr(m.expected_balance)}</td>
                    <td className="num text-right">{inr(m.statement_balance)}</td>
                    <td className="num text-right text-red-700">{inr(m.difference)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>

        <Card title="Review Flags" testid="review-flags">
          {a.review_flags.length === 0 ? (
            <p className="text-sm text-muted-foreground">No review flags raised.</p>
          ) : (
            <ul className="space-y-3">
              {a.review_flags.map((f) => (
                <li key={f.type} className="flex gap-3 rounded-md border border-border p-3">
                  <AlertTriangle className={`mt-0.5 h-4 w-4 shrink-0 ${
                    f.severity === "high" ? "text-red-600" : f.severity === "medium" ? "text-amber-600" : "text-blue-600"}`} />
                  <div>
                    <p className="text-sm font-semibold">{f.type}</p>
                    <p className="text-xs text-muted-foreground">{f.detail}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-xs text-muted-foreground">
            These are review indicators, not definitive risk classifications.
          </p>
        </Card>
      </div>

      <Card title="Insights" testid="insights">
        <ul className="space-y-2 text-sm">
          {a.insights.map((i) => (
            <li key={i} className="flex gap-2"><TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{i}</li>
          ))}
        </ul>
      </Card>

      {a.duplicates.length > 0 && (
        <Card title="Potential Duplicates" testid="duplicates-panel">
          <p className="mb-3 flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="h-3 w-3" /> Nothing is deleted automatically. Resolve each row from the
            transaction table using Keep, Mark as Duplicate or Exclude from Analysis.
          </p>
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground">
              <th className="py-2 text-left">Date</th><th className="text-left">Description</th>
              <th className="text-right">Amount</th><th className="text-right">Resolution</th>
            </tr></thead>
            <tbody>
              {a.duplicates.map((d, i) => (
                <tr key={i} className="border-b border-border/60">
                  <td className="py-2 text-xs">{fmtDate(d.date)}</td>
                  <td className="max-w-[320px] truncate">{d.description}</td>
                  <td className="num text-right">{inr(d.amount)}</td>
                  <td className="text-right">
                    <Badge variant="outline" className={
                      d.excluded ? "border-red-200 bg-red-50 text-red-700"
                        : d.resolution === "duplicate" ? "border-warn-border bg-warn text-warn-foreground"
                          : d.resolution === "keep" ? "border-green-200 bg-ok text-ok-foreground"
                            : "bg-muted text-muted-foreground"}>
                      {d.excluded ? "Excluded" : d.resolution === "unresolved" ? "Needs review" : d.resolution}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
};
